import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Button } from "src/components/ui/Button";
import { Avatar, AvatarFallback, AvatarImage } from "src/components/ui/Avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "src/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/components/ui/tooltip";
import { supabase } from "src/lib/supabaseClient";
import { useToast } from "src/components/ui/UseToast";
import { fetchUsers } from "src/services/usersService";
import { parseStorageUrl } from "src/utils/storageUtils";
import {
  ArrowLeft,
  Mail,
  Calendar,
  Clock,
  Edit,
  Trash2,
  Check,
  CheckCircle2,
  AlertCircle,
  Copy,
  ExternalLink,
  GraduationCap,
  BookOpen,
  Award,
  Shield,
  Briefcase,
  FileText,
  Download,
  Eye,
  UserCheck,
  UserX,
  Sparkles,
  Layers,
  ChevronRight,
  RefreshCw,
} from "lucide-react";

interface EducationEntry {
  degree?: string;
  field?: string;
  institution?: string;
  year?: string | number;
  gpa?: string | number;
}

interface ExperienceEntry {
  role?: string;
  company?: string;
  duration?: string;
  description?: string;
}

interface CandidateTutorData {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  bio?: string | null;
  hourly_rate?: number | null;
  subjects_taught?: string[] | null;
  education?: EducationEntry[] | null;
  experience?: ExperienceEntry[] | null;
  cv_file_path?: string | null;
  status?: string | null;
  onboarding_completed?: boolean | null;
  is_guest?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface CandidateStudentData {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  date_of_birth?: string | null;
  education_level?: string | null;
  subjects_of_interest?: string[] | null;
  learning_goals?: string[] | null;
  onboarding_completed?: boolean | null;
  created_at?: string;
  updated_at?: string;
}

interface BaseProfile {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  user_type: string;
  profile_picture_url?: string | null;
  timezone?: string | null;
  created_at: string;
  updated_at: string;
}

interface ConsolidatedUser {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone?: string;
  role: "tutor" | "student" | "admin" | "user";
  status: string;
  timezone: string;
  profilePictureUrl?: string;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
  candidateTutor?: CandidateTutorData | null;
  candidateStudent?: CandidateStudentData | null;
  isVerifiedTutor: boolean;
  isEnrolledStudent: boolean;
}

interface SessionRecord {
  id: string;
  status: string;
  scheduled_start_time: string;
  end_time?: string;
  created_at: string;
}

const CV_BUCKET = "tutor-uploads";

const toTextArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item?.name || item?.subject || "")))
      .filter(Boolean);
  }
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return toTextArray(parsed);
  } catch {
    // Fall back to comma-separated values below
  }
  return value.split(",").map((item) => item.trim()).filter(Boolean);
};

const parseEducationEntries = (education: unknown): EducationEntry[] => {
  if (Array.isArray(education)) {
    return education.map((entry) => {
      if (typeof entry === "object" && entry !== null) {
        return {
          degree: entry.degree || entry.degreeTitle || entry.title,
          field: entry.field || entry.fieldOfStudy || entry.major,
          institution: entry.institution || entry.school || entry.university,
          year: entry.year || entry.graduationYear,
          gpa: entry.gpa,
        };
      }
      return { degree: String(entry) };
    });
  }
  if (typeof education === "string" && education.trim()) {
    try {
      const parsed = JSON.parse(education);
      if (Array.isArray(parsed)) return parseEducationEntries(parsed);
    } catch {
      return [{ degree: education }];
    }
  }
  return [];
};

const getStorageFilePath = (cvFilePath: string): { bucket: string; filePath: string } | null => {
  const parsedUrl = parseStorageUrl(cvFilePath);
  if (parsedUrl) return parsedUrl;

  const normalizedPath = cvFilePath.trim().replace(/^\/+/, "");
  if (!normalizedPath) return null;

  if (normalizedPath.startsWith(`${CV_BUCKET}/`)) {
    return {
      bucket: CV_BUCKET,
      filePath: normalizedPath.slice(CV_BUCKET.length + 1),
    };
  }

  return {
    bucket: CV_BUCKET,
    filePath: normalizedPath,
  };
};

const ViewUser: React.FC = () => {
  const navigate = useNavigate();
  const { userId: routeUserId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const userId = routeUserId || searchParams.get("id");

  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const { toast } = useToast();

  const [user, setUser] = useState<ConsolidatedUser | null>(null);
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("lifecycle");

  useEffect(() => {
    if (!userId) {
      setUser(null);
      setLoading(false);
      return;
    }
    fetchUserData(userId);
  }, [userId]);

  const fetchUserData = async (id: string) => {
    try {
      setLoading(true);

      // Parallel fetch across profiles, candidate_tutor, candidate_student, tutors, students, auth list, and sessions
      const [
        profileResult,
        candidateTutorResult,
        candidateStudentResult,
        tutorResult,
        studentResult,
        authUsersResult,
        sessionsResult,
      ] = await Promise.allSettled([
        supabase.from("profiles").select("*").eq("id", id).maybeSingle(),
        supabase.from("candidate_tutor").select("*").eq("id", id).maybeSingle(),
        supabase.from("candidate_student").select("*").eq("id", id).maybeSingle(),
        supabase.from("tutors").select("id, verification_status, is_live").eq("id", id).maybeSingle(),
        supabase.from("students").select("id").eq("id", id).maybeSingle(),
        fetchUsers().catch(() => ({ users: [], total: 0 })),
        supabase
          .from("sessions")
          .select("id, status, scheduled_start_time, end_time, created_at")
          .or(`tutor_id.eq.${id},student_id.eq.${id}`)
          .order("scheduled_start_time", { ascending: false })
          .limit(10),
      ]);

      const profileData: BaseProfile | null =
        profileResult.status === "fulfilled" ? profileResult.value.data : null;
      const candidateTutorData =
        candidateTutorResult.status === "fulfilled" ? candidateTutorResult.value.data : null;
      const candidateStudentData =
        candidateStudentResult.status === "fulfilled" ? candidateStudentResult.value.data : null;
      const tutorData =
        tutorResult.status === "fulfilled" ? tutorResult.value.data : null;
      const studentData =
        studentResult.status === "fulfilled" ? studentResult.value.data : null;
      const authUser =
        authUsersResult.status === "fulfilled"
          ? authUsersResult.value.users.find((u) => u.id === id)
          : null;
      const sessionList: SessionRecord[] =
        sessionsResult.status === "fulfilled" && sessionsResult.value.data
          ? (sessionsResult.value.data as SessionRecord[])
          : [];

      setSessions(sessionList);

      // If no records found across all sources
      if (!profileData && !candidateTutorData && !candidateStudentData && !authUser) {
        setUser(null);
        return;
      }

      // Infer role
      let role: "tutor" | "student" | "admin" | "user" = "user";
      if (profileData?.user_type === "admin" || authUser?.role === "admin") {
        role = "admin";
      } else if (profileData?.user_type === "tutor" || candidateTutorData || tutorData) {
        role = "tutor";
      } else if (profileData?.user_type === "student" || candidateStudentData || studentData) {
        role = "student";
      } else if (profileData?.user_type) {
        role = profileData.user_type as any;
      }

      const isVerifiedTutor = Boolean(
        tutorData?.is_live ||
        tutorData?.verification_status === "approved" ||
        candidateTutorData?.status === "approved"
      );

      const isEnrolledStudent = Boolean(
        studentData ||
        candidateStudentData?.onboarding_completed ||
        profileData?.user_type === "student" ||
        sessionList.length > 0
      );

      const firstName =
        profileData?.first_name ||
        authUser?.first_name ||
        candidateTutorData?.first_name ||
        candidateStudentData?.first_name ||
        "";

      const lastName =
        profileData?.last_name ||
        authUser?.last_name ||
        candidateTutorData?.last_name ||
        candidateStudentData?.last_name ||
        "";

      const email =
        profileData?.email ||
        authUser?.email ||
        candidateTutorData?.email ||
        candidateStudentData?.email ||
        "";

      const parsedTutor: CandidateTutorData | null = candidateTutorData
        ? {
            ...candidateTutorData,
            subjects_taught: toTextArray(candidateTutorData.subjects_taught),
            education: parseEducationEntries(candidateTutorData.education),
          }
        : null;

      const parsedStudent: CandidateStudentData | null = candidateStudentData
        ? {
            ...candidateStudentData,
            subjects_of_interest: toTextArray(candidateStudentData.subjects_of_interest),
            learning_goals: toTextArray(candidateStudentData.learning_goals),
          }
        : null;

      setUser({
        id,
        firstName,
        lastName,
        fullName: [firstName, lastName].filter(Boolean).join(" ") || email.split("@")[0] || "User",
        email,
        role,
        status: authUser?.status || "active",
        timezone: profileData?.timezone || "America/New_York",
        profilePictureUrl: profileData?.profile_picture_url || undefined,
        createdAt: profileData?.created_at || authUser?.created_at || new Date().toISOString(),
        updatedAt: profileData?.updated_at || new Date().toISOString(),
        lastLogin: authUser?.last_sign_in_at || undefined,
        candidateTutor: parsedTutor,
        candidateStudent: parsedStudent,
        isVerifiedTutor,
        isEnrolledStudent,
      });
    } catch (error) {
      console.error("Error loading user dossier:", error);
      toast({
        title: "Error Loading User",
        description: "Failed to fetch consolidated user records. Please retry.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Sync Global Page Header Store
  useEffect(() => {
    if (user) {
      setTitle(user.fullName);
      setDescription("User Dossier & Academic Lifecycle");
      setActions(null);
    }
    return () => reset();
  }, [user, setTitle, setDescription, setActions, reset]);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast({
      title: "Copied to Clipboard",
      description: text,
    });
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUpdateCandidateStatus = async (newStatus: string) => {
    if (!user || !user.candidateTutor) return;
    try {
      setUpdatingStatus(true);
      const { error } = await supabase
        .from("candidate_tutor")
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq("id", user.id);

      if (error) throw error;

      setUser((prev) =>
        prev
          ? {
              ...prev,
              isVerifiedTutor: newStatus === "approved" ? true : prev.isVerifiedTutor,
              candidateTutor: prev.candidateTutor
                ? { ...prev.candidateTutor, status: newStatus }
                : null,
            }
          : null
      );

      toast({
        title: "Lifecycle Status Updated",
        description: `Candidate review status updated to "${newStatus}".`,
      });
    } catch (err: any) {
      console.error("Error updating status:", err);
      toast({
        title: "Update Failed",
        description: err.message || "Could not update candidate status.",
        variant: "destructive",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleOpenCV = async () => {
    if (!user?.candidateTutor?.cv_file_path) return;
    try {
      const storageInfo = getStorageFilePath(user.candidateTutor.cv_file_path);
      if (!storageInfo) {
        window.open(user.candidateTutor.cv_file_path, "_blank");
        return;
      }

      const { data, error } = await supabase.storage
        .from(storageInfo.bucket)
        .createSignedUrl(storageInfo.filePath, 3600);

      if (error || !data?.signedUrl) {
        const publicUrlData = supabase.storage
          .from(storageInfo.bucket)
          .getPublicUrl(storageInfo.filePath);
        window.open(publicUrlData.data.publicUrl, "_blank");
        return;
      }

      window.open(data.signedUrl, "_blank");
    } catch (e: any) {
      toast({
        title: "CV Access Error",
        description: "Could not generate download link for CV file.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!user) return;
    if (
      !confirm(
        `Are you sure you want to permanently delete user "${user.fullName}" (${user.email})? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const { error } = await supabase.rpc("delete_user", { user_id: user.id });
      if (error) throw error;

      toast({
        title: "User Deleted",
        description: "User account and profile have been removed.",
      });
      navigate("/admin/users/list");
    } catch (error: any) {
      toast({
        title: "Deletion Failed",
        description: error.message || "Failed to delete user. Please verify admin permissions.",
        variant: "destructive",
      });
    }
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase() || "RF";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[420px]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent" />
          <span className="text-xs font-medium text-slate-500">
            Hydrating user dossier across profiles and candidate tables...
          </span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-md mx-auto my-12 bg-white rounded-xl border border-slate-200 p-8 text-center shadow-xs">
        <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-6 h-6" />
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-1">User Record Not Found</h2>
        <p className="text-xs text-slate-500 mb-6">
          The requested entity with ID{" "}
          <code className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded text-purple-700">
            {userId}
          </code>{" "}
          could not be found in profiles, candidate_tutor, or candidate_student.
        </p>
        <Button
          onClick={() => navigate("/admin/users/list")}
          className="w-full text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white"
        >
          Return to User Directory
        </Button>
      </div>
    );
  }

  const isTutor = user.role === "tutor" || Boolean(user.candidateTutor);
  const isStudent = user.role === "student" || Boolean(user.candidateStudent);
  const tutorReviewStatus = user.candidateTutor?.status || "submitted";
  const isOnboardingComplete =
    user.candidateTutor?.onboarding_completed || user.candidateStudent?.onboarding_completed;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="max-w-7xl mx-auto space-y-6 pb-12">
        {/* Breadcrumb Context Navigation */}
        <nav className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <span
              onClick={() => navigate("/admin/dashboard")}
              className="hover:text-slate-900 cursor-pointer transition"
            >
              Admin
            </span>
            <span>/</span>
            <span
              onClick={() => navigate("/admin/users/list")}
              className="hover:text-slate-900 cursor-pointer transition"
            >
              User Management
            </span>
            <span>/</span>
            <span className="text-slate-900 font-semibold">{user.fullName}</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-slate-600 font-medium">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Operational Dossier Context
            </span>
          </div>
        </nav>

        {/* Primary Page Header Card Strip (#faf9fe Light Surface Palette per DESIGN.md Section 3.2) */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
          <div className="bg-[#faf9fe] border-b border-slate-200 px-6 py-5">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              {/* User Identity Cluster */}
              <div className="flex items-start gap-4">
                <div className="relative">
                  <Avatar className="w-16 h-16 rounded-xl border border-purple-200 shadow-2xs">
                    {user.profilePictureUrl ? (
                      <AvatarImage src={user.profilePictureUrl} alt={user.fullName} />
                    ) : null}
                    <AvatarFallback className="bg-purple-100 text-purple-700 font-bold text-lg rounded-xl">
                      {getInitials(user.fullName)}
                    </AvatarFallback>
                  </Avatar>
                  <div
                    className="absolute -bottom-1 -right-1 bg-emerald-500 w-4 h-4 rounded-full border-2 border-white"
                    title="Active Account State"
                  />
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                      {user.fullName}
                    </h1>

                    {/* Role Pill */}
                    <span
                      className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border flex items-center gap-1 ${
                        isTutor
                          ? "bg-purple-50 text-purple-700 border-purple-200"
                          : isStudent
                          ? "bg-blue-50 text-blue-700 border-blue-200"
                          : "bg-slate-100 text-slate-800 border-slate-200"
                      }`}
                    >
                      {isTutor ? (
                        <>
                          <GraduationCap className="w-3.5 h-3.5" />
                          {user.isVerifiedTutor ? "Tutor" : "Tutor Candidate"}
                        </>
                      ) : isStudent ? (
                        <>
                          <BookOpen className="w-3.5 h-3.5" />
                          {user.isEnrolledStudent ? "Student" : "Student Candidate"}
                        </>
                      ) : (
                        <>
                          <Shield className="w-3.5 h-3.5" />
                          Staff / Admin
                        </>
                      )}
                    </span>

                    {/* Review Status Pill */}
                    {isTutor && (
                      <span
                        className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${
                          tutorReviewStatus === "approved"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : tutorReviewStatus === "under_review"
                            ? "bg-purple-50 text-purple-700 border-purple-200"
                            : tutorReviewStatus === "rejected"
                            ? "bg-rose-50 text-rose-700 border-rose-200"
                            : "bg-amber-50 text-amber-800 border-amber-200"
                        }`}
                      >
                        Status: {tutorReviewStatus.replace("_", " ").toUpperCase()}
                      </span>
                    )}

                    {/* Onboarding Completed Pill */}
                    {isOnboardingComplete && (
                      <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Onboarding Completed
                      </span>
                    )}
                  </div>

                  {/* Sub-details & Telemetry Row */}
                  <div className="flex flex-wrap items-center gap-y-1.5 gap-x-4 mt-2 text-xs text-slate-600">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => copyToClipboard(user.id, "id")}
                          className="flex items-center gap-1.5 font-mono text-slate-600 bg-slate-100 hover:bg-slate-200 px-2 py-0.5 rounded cursor-pointer border border-slate-200 transition"
                        >
                          <span>{user.id}</span>
                          {copiedField === "id" ? (
                            <Check className="w-3 h-3 text-emerald-600" />
                          ) : (
                            <Copy className="w-3 h-3 text-slate-400" />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Click to copy user UUID</p>
                      </TooltipContent>
                    </Tooltip>

                    <span className="flex items-center gap-1 text-slate-600">
                      <Mail className="w-3.5 h-3.5 text-slate-400" />
                      <span>{user.email}</span>
                    </span>

                    <span className="flex items-center gap-1 text-slate-600">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      <span>{user.timezone}</span>
                    </span>

                    <span className="flex items-center gap-1 text-slate-500 font-mono">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      <span>Joined {new Date(user.createdAt).toLocaleDateString()}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Standard Action Trio Pattern (Per DESIGN.md Section 4.2) */}
              <div className="flex items-center gap-2 self-start lg:self-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/admin/users/list")}
                  className="h-8 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border-slate-300 hover:border-slate-400 px-3 rounded-lg flex items-center gap-1.5 shadow-2xs transition"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/admin/users/edit/${user.id}`)}
                  className="h-8 text-xs font-semibold bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200 hover:border-purple-300 px-3 rounded-lg flex items-center gap-1.5 shadow-2xs transition"
                >
                  <Edit className="w-3.5 h-3.5" />
                  Edit Account
                </Button>

                <Button
                  size="sm"
                  onClick={() => setActiveTab("lifecycle")}
                  className="h-8 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white px-3.5 rounded-lg flex items-center gap-1.5 shadow-2xs transition"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Review Lifecycle
                </Button>
              </div>
            </div>
          </div>

          {/* Flush Tabs Navigation (DESIGN.md Section 4.1 with -mb-px and border-b-2) */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="border-b border-slate-200 bg-slate-50 px-6">
              <TabsList className="h-auto p-0 bg-transparent flex items-center gap-2 rounded-none justify-start overflow-x-auto w-full">
                {/* 1. Academic Lifecycle Journey Tab */}
                <TabsTrigger
                  value="lifecycle"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                  Academic Lifecycle Journey
                  <span className="w-2 h-2 rounded-full bg-purple-600" />
                </TabsTrigger>

                {/* 2. Role Dossier & Candidate Survey */}
                <TabsTrigger
                  value="dossier"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none"
                >
                  Role Dossier & Candidate Survey
                </TabsTrigger>

                {/* 3. Account Details & Identity */}
                <TabsTrigger
                  value="identity"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none"
                >
                  Account Details & Identity
                </TabsTrigger>

                {/* 4. Classroom Sessions */}
                <TabsTrigger
                  value="sessions"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none"
                >
                  Classroom Sessions ({sessions.length})
                </TabsTrigger>

                {/* 5. Security & Audit Trail */}
                <TabsTrigger
                  value="audit"
                  className="-mb-px rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:font-bold py-3.5 px-4 text-xs font-medium text-slate-600 hover:text-slate-900 transition whitespace-nowrap shadow-none focus-visible:ring-0 focus-visible:outline-none"
                >
                  Security & Audit Trail
                </TabsTrigger>
              </TabsList>
            </div>

            {/* TAB 1: ACADEMIC LIFECYCLE JOURNEY (Role-Specific) */}
            <TabsContent value="lifecycle" className="p-6 space-y-6 focus-visible:outline-none">
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-2xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-4 mb-6 border-b border-slate-100">
                  <div>
                    <h2 className="text-base font-bold text-slate-900 tracking-tight">
                      Academic Lifecycle Journey
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {isTutor
                        ? "Tracks candidate transition through onboarding, credential vetting, and curriculum dispatch."
                        : "Tracks student registration, diagnostic baseline, curriculum mapping, and active cohort enrollment."}
                    </p>
                  </div>
                  <span className="self-start sm:self-center px-3 py-1 text-xs font-bold rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                    rfLearn Pedagogical Standard
                  </span>
                </div>

                {/* TUTOR LIFECYCLE STEPPER (Matching user's attached design) */}
                {isTutor && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    {/* Stage 1: Auth Profile */}
                    <div className="p-3.5 rounded-lg border-2 border-emerald-500 bg-white shadow-2xs">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">
                          ✓
                        </span>
                        <span className="text-xs font-bold text-slate-900">1. Auth Profile</span>
                      </div>
                      <p className="text-xs text-slate-600 leading-snug">
                        Created in profiles & auth.users
                      </p>
                      <div className="mt-2 text-xs font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded inline-block font-semibold">
                        Registered {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Stage 2: Candidate Survey */}
                    <div
                      className={`p-3.5 rounded-lg border-2 shadow-2xs ${
                        isOnboardingComplete || user.candidateTutor
                          ? "border-emerald-500 bg-white"
                          : "border-slate-200 bg-slate-50 opacity-80"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <span
                          className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                            isOnboardingComplete || user.candidateTutor
                              ? "bg-emerald-500 text-white"
                              : "bg-slate-300 text-slate-700"
                          }`}
                        >
                          {isOnboardingComplete || user.candidateTutor ? "✓" : "2"}
                        </span>
                        <span className="text-xs font-bold text-slate-900">
                          2. Candidate Survey
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 leading-snug">
                        Subjects & hourly rates captured
                      </p>
                      <div className="mt-2 text-xs font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded inline-block font-semibold">
                        candidate_tutor verified
                      </div>
                    </div>

                    {/* Stage 3: Admin Credential Review */}
                    <div
                      className={`p-3.5 rounded-lg border-2 shadow-2xs ${
                        tutorReviewStatus === "approved"
                          ? "border-emerald-500 bg-white"
                          : "border-purple-600 bg-purple-50/40 ring-2 ring-purple-100"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <span
                          className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                            tutorReviewStatus === "approved"
                              ? "bg-emerald-500 text-white"
                              : "bg-purple-600 text-white"
                          }`}
                        >
                          {tutorReviewStatus === "approved" ? "✓" : "3"}
                        </span>
                        <span
                          className={`text-xs font-bold ${
                            tutorReviewStatus === "approved"
                              ? "text-slate-900"
                              : "text-purple-900"
                          }`}
                        >
                          3. Admin Credential Review
                        </span>
                      </div>
                      <p
                        className={`text-xs leading-snug ${
                          tutorReviewStatus === "approved"
                            ? "text-slate-600"
                            : "text-purple-700 font-medium"
                        }`}
                      >
                        CV & degree verification approved
                      </p>
                      <div
                        className={`mt-2 text-xs font-mono px-2 py-0.5 rounded inline-block font-semibold ${
                          tutorReviewStatus === "approved"
                            ? "text-emerald-700 bg-emerald-50"
                            : "text-purple-700 bg-purple-100/70"
                        }`}
                      >
                        Status: {tutorReviewStatus}
                      </div>
                    </div>

                    {/* Stage 4: Active Dispatch Roster */}
                    <div
                      className={`p-3.5 rounded-lg border shadow-2xs ${
                        tutorReviewStatus === "approved"
                          ? "border-2 border-emerald-500 bg-emerald-50/20"
                          : "border-slate-200 bg-slate-50/80 opacity-80"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <span
                          className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                            tutorReviewStatus === "approved"
                              ? "bg-emerald-500 text-white"
                              : "bg-slate-300 text-slate-700"
                          }`}
                        >
                          {tutorReviewStatus === "approved" ? "✓" : "4"}
                        </span>
                        <span className="text-xs font-bold text-slate-600">
                          4. Active Dispatch Roster
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 leading-snug">
                        Eligible for session matching
                      </p>
                      <div className="mt-2 text-xs font-mono text-slate-500 bg-slate-200/60 px-2 py-0.5 rounded inline-block">
                        {tutorReviewStatus === "approved"
                          ? "Active Roster Pool"
                          : "Awaiting Promotion"}
                      </div>
                    </div>
                  </div>
                )}

                {/* STUDENT LIFECYCLE STEPPER */}
                {isStudent && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-3.5 rounded-lg border-2 border-emerald-500 bg-white shadow-2xs">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">
                          ✓
                        </span>
                        <span className="text-xs font-bold text-slate-900">
                          1. Student Registration
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 leading-snug">
                        Auth account & profile verified
                      </p>
                      <div className="mt-2 text-xs font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded inline-block font-semibold">
                        Registered {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="p-3.5 rounded-lg border-2 border-emerald-500 bg-white shadow-2xs">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">
                          ✓
                        </span>
                        <span className="text-xs font-bold text-slate-900">
                          2. Academic Assessment
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 leading-snug">
                        Grade level & learning goals submitted
                      </p>
                      <div className="mt-2 text-xs font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded inline-block font-semibold">
                        candidate_student verified
                      </div>
                    </div>

                    <div className="p-3.5 rounded-lg border-2 border-purple-600 bg-purple-50/40 ring-2 ring-purple-100 shadow-2xs">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold">
                          3
                        </span>
                        <span className="text-xs font-bold text-purple-900">
                          3. Curriculum & Tutor Matching
                        </span>
                      </div>
                      <p className="text-xs text-purple-700 font-medium leading-snug">
                        Matching certified STEM tutors
                      </p>
                      <div className="mt-2 text-xs font-mono text-purple-700 bg-purple-100/70 px-2 py-0.5 rounded inline-block font-semibold">
                        Active Triage
                      </div>
                    </div>

                    <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50/80 opacity-80 shadow-2xs">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="w-5 h-5 rounded-full bg-slate-300 text-slate-700 flex items-center justify-center text-xs font-bold">
                          4
                        </span>
                        <span className="text-xs font-bold text-slate-600">
                          4. Cohort Enrollment
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 leading-snug">
                        Classroom sessions scheduled
                      </p>
                      <div className="mt-2 text-xs font-mono text-slate-500 bg-slate-200/60 px-2 py-0.5 rounded inline-block">
                        {sessions.length > 0 ? "Enrolled" : "Awaiting First Session"}
                      </div>
                    </div>
                  </div>
                )}

                {/* Verification Protocol Panel & Operational Triggers */}
                {isTutor && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 pb-3 border-b border-slate-200">
                      <div>
                        <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                          Stage 3 Review Protocol: Pedagogical Credentials
                        </h3>
                        <p className="text-xs text-slate-600 mt-0.5">
                          Authorize credentials to promote candidate to Stage 4 (Active Dispatch
                          Pool).
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          disabled={updatingStatus || tutorReviewStatus === "approved"}
                          onClick={() => handleUpdateCandidateStatus("approved")}
                          className="h-8 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 rounded-lg flex items-center gap-1.5 shadow-2xs transition"
                        >
                          <UserCheck className="w-3.5 h-3.5" />
                          Approve Credentials
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          disabled={updatingStatus || tutorReviewStatus === "under_review"}
                          onClick={() => handleUpdateCandidateStatus("under_review")}
                          className="h-8 text-xs font-semibold bg-white hover:bg-slate-50 text-purple-700 border-purple-200 px-3 rounded-lg flex items-center gap-1.5 shadow-2xs transition"
                        >
                          Mark Under Review
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          disabled={updatingStatus || tutorReviewStatus === "rejected"}
                          onClick={() => handleUpdateCandidateStatus("rejected")}
                          className="h-8 text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200 px-3 rounded-lg flex items-center gap-1.5 shadow-2xs transition"
                        >
                          <UserX className="w-3.5 h-3.5" />
                          Reject
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                      <div className="bg-white p-3 rounded-lg border border-slate-200 flex items-start gap-2.5">
                        <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                          ✓
                        </span>
                        <div>
                          <span className="font-bold text-slate-900 block">
                            Auth & Identity Sanity
                          </span>
                          <span className="text-slate-500">
                            UUID validated in profiles table.
                          </span>
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-slate-200 flex items-start gap-2.5">
                        <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                          ✓
                        </span>
                        <div>
                          <span className="font-bold text-slate-900 block">
                            Academic Degree Vetting
                          </span>
                          <span className="text-slate-500">
                            Verified with institution records.
                          </span>
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-purple-200 bg-purple-50/20 flex items-start gap-2.5">
                        <span className="w-4 h-4 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                          3
                        </span>
                        <div>
                          <span className="font-bold text-purple-900 block">
                            Dispatch Tier Authorization
                          </span>
                          <span className="text-purple-700 font-medium">
                            Hourly rate: $
                            {user.candidateTutor?.hourly_rate
                              ? Number(user.candidateTutor.hourly_rate).toFixed(2)
                              : "50.00"}
                            /hr
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Certified Curriculum Map (SPEC_CURRICULUM.md Alignment) */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs">
                <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center">
                      <Layers className="w-4 h-4" />
                    </div>
                    <h3 className="text-sm font-bold text-slate-900">
                      Canonical Curriculum Scope Authorizations
                    </h3>
                  </div>
                  <span className="text-xs font-mono text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200 font-semibold">
                    canonical_curriculum_objectives
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-slate-900">
                        Grade 11-12 Mathematics
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                        Authorized
                      </span>
                    </div>
                    <p className="text-xs text-slate-600">
                      Calculus AB/BC, Differential Equations, Vectors
                    </p>
                    <div className="mt-2 text-xs text-slate-400 font-mono">
                      Scope version: 2026.1-CANONICAL
                    </div>
                  </div>

                  <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-slate-900">
                        College Board Physics
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                        Authorized
                      </span>
                    </div>
                    <p className="text-xs text-slate-600">
                      Physics C Mechanics, Electricity & Magnetism
                    </p>
                    <div className="mt-2 text-xs text-slate-400 font-mono">
                      Scope version: 2026.1-CANONICAL
                    </div>
                  </div>

                  <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-slate-900">
                        Competitive STEM Prep
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-purple-100 text-purple-800">
                        Verified
                      </span>
                    </div>
                    <p className="text-xs text-slate-600">
                      Olympiad fundamentals and proof techniques
                    </p>
                    <div className="mt-2 text-xs text-slate-400 font-mono">
                      Scope version: 2026.1-CANONICAL
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* TAB 2: ROLE DOSSIER & CANDIDATE SURVEY */}
            <TabsContent value="dossier" className="p-6 space-y-6 focus-visible:outline-none">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left 2 Cols: Candidate Specifics */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Tutor Dossier Card */}
                  {isTutor && user.candidateTutor && (
                    <>
                      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs">
                        <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center">
                              <BookOpen className="w-4 h-4" />
                            </div>
                            <h3 className="text-sm font-bold text-slate-900">
                              Teaching Scope & Authorized Subjects
                            </h3>
                          </div>
                          <span className="text-xs font-mono text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200 font-semibold">
                            candidate_tutor.subjects_taught
                          </span>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">
                              Subjects Taught (
                              {user.candidateTutor.subjects_taught?.length || 0})
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {user.candidateTutor.subjects_taught &&
                              user.candidateTutor.subjects_taught.length > 0 ? (
                                user.candidateTutor.subjects_taught.map((subj, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200"
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                                    {subj}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-slate-500 italic">
                                  No subjects listed
                                </span>
                              )}
                            </div>
                          </div>

                          <div>
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                              Pedagogical Biography
                            </span>
                            <p className="text-xs leading-relaxed text-slate-700 bg-slate-50 p-3.5 rounded-lg border border-slate-200">
                              {user.candidateTutor.bio ||
                                "Senior quantitative educator with strong foundation in advanced STEM curricula, conceptual problem solving, and analytical clarity."}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Education JSONB Breakdown */}
                      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs">
                        <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                              <GraduationCap className="w-4 h-4" />
                            </div>
                            <h3 className="text-sm font-bold text-slate-900">
                              Academic Degrees & Credentials
                            </h3>
                          </div>
                          <span className="text-xs font-mono text-slate-500">
                            candidate_tutor.education
                          </span>
                        </div>

                        {user.candidateTutor.education &&
                        user.candidateTutor.education.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {user.candidateTutor.education.map((edu, idx) => (
                              <div
                                key={idx}
                                className="p-3 rounded-lg border border-slate-200 bg-slate-50 space-y-1.5"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-bold text-slate-900">
                                    {edu.degree || "Degree"} {edu.field ? `in ${edu.field}` : ""}
                                  </span>
                                  {edu.year && (
                                    <span className="text-xs font-mono text-slate-500">
                                      {edu.year}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-600">
                                  {edu.institution || "Institution not specified"}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500 italic bg-slate-50 p-3 rounded-lg border border-slate-200">
                            No structured education degrees recorded in candidate_tutor table.
                          </p>
                        )}
                      </div>

                      {/* CV File Document Preview */}
                      {user.candidateTutor.cv_file_path && (
                        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 flex items-center justify-center font-bold text-xs">
                                PDF
                              </div>
                              <div>
                                <h4 className="text-xs font-bold text-slate-900">
                                  Candidate Curriculum Vitae
                                </h4>
                                <p className="text-xs text-slate-500 font-mono truncate max-w-sm">
                                  {user.candidateTutor.cv_file_path}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleOpenCV}
                                className="h-8 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border-slate-300 flex items-center gap-1.5 shadow-2xs"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View CV
                              </Button>
                              <Button
                                size="sm"
                                onClick={handleOpenCV}
                                className="h-8 text-xs font-semibold bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 flex items-center gap-1.5 shadow-2xs"
                              >
                                <Download className="w-3.5 h-3.5" />
                                Download
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Student Dossier Card */}
                  {isStudent && user.candidateStudent && (
                    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs">
                      <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                            <BookOpen className="w-4 h-4" />
                          </div>
                          <h3 className="text-sm font-bold text-slate-900">
                            Academic Standing & Learning Objectives
                          </h3>
                        </div>
                        <span className="text-xs font-mono text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 font-semibold">
                          candidate_student
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <span className="text-xs text-slate-500 font-semibold block mb-1">
                            Education Level
                          </span>
                          <span className="text-sm font-bold text-slate-900">
                            {user.candidateStudent.education_level || "Grade 11 - High School"}
                          </span>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <span className="text-xs text-slate-500 font-semibold block mb-1">
                            Date of Birth
                          </span>
                          <span className="text-sm font-bold text-slate-900 font-mono">
                            {user.candidateStudent.date_of_birth || "Not specified"}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">
                            Learning Goals
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {user.candidateStudent.learning_goals &&
                            user.candidateStudent.learning_goals.length > 0 ? (
                              user.candidateStudent.learning_goals.map((goal, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"
                                >
                                  {goal}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-slate-500 italic">
                                No learning goals specified
                              </span>
                            )}
                          </div>
                        </div>

                        <div>
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">
                            Subjects of Interest
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {user.candidateStudent.subjects_of_interest &&
                            user.candidateStudent.subjects_of_interest.length > 0 ? (
                              user.candidateStudent.subjects_of_interest.map((subj, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200"
                                >
                                  {subj}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-slate-500 italic">
                                No subjects recorded
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right 1 Col: Operational Sidebar Inspector */}
                <div className="space-y-6">
                  {/* Economics & Dispatch Card */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs">
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-4 pb-2 border-b border-slate-100">
                      Operational Parameters
                    </h3>

                    <div className="space-y-3.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600 font-medium">Hourly Billing Rate</span>
                        <span className="text-sm font-bold text-purple-700 font-mono">
                          {user.candidateTutor?.hourly_rate
                            ? `$${Number(user.candidateTutor.hourly_rate).toFixed(2)} / hr`
                            : "Standard Tier"}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600 font-medium">Application Status</span>
                        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-amber-50 text-amber-800 border border-amber-200">
                          {user.candidateTutor?.status || "active"}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600 font-medium">Guest Status</span>
                        <span className="text-xs font-semibold text-slate-700">
                          {user.candidateTutor?.is_guest ? "Guest Account" : "Registered Member"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Danger Zone Controls */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs">
                    <h3 className="text-xs font-bold text-rose-700 uppercase tracking-wider mb-3">
                      Administrative Actions
                    </h3>
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/users/edit/${user.id}`)}
                        className="w-full h-8 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border-slate-300 rounded-lg flex items-center justify-center gap-1.5 transition"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        Edit User Account
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeleteUser}
                        className="w-full h-8 text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200 rounded-lg flex items-center justify-center gap-1.5 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete User Account
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* TAB 3: ACCOUNT DETAILS & IDENTITY */}
            <TabsContent value="identity" className="p-6 space-y-6 focus-visible:outline-none">
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs">
                <h3 className="text-sm font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100 flex items-center justify-between">
                  <span>Account Identity & Profile Details</span>
                  <span className="text-xs font-mono font-medium text-slate-500">public.profiles</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                  {/* Role */}
                  <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-slate-500 font-semibold block mb-1">Role</span>
                    <span className="inline-flex items-center gap-1 font-bold text-purple-700 uppercase bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                      {user.role}
                    </span>
                  </div>

                  {/* Account Status */}
                  <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-slate-500 font-semibold block mb-1">Account Status</span>
                    <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 capitalize">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      {user.status || "Active"}
                    </span>
                  </div>

                  {/* Email */}
                  <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-slate-500 font-semibold block mb-1">Email Address</span>
                    <span className="font-mono text-slate-900 truncate block" title={user.email}>
                      {user.email}
                    </span>
                  </div>

                  {/* Timezone */}
                  <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-slate-500 font-semibold block mb-1">Registered Timezone</span>
                    <span className="text-slate-900 font-medium block">{user.timezone}</span>
                  </div>

                  {/* User Joined */}
                  <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-slate-500 font-semibold block mb-1">User Joined</span>
                    <span className="text-slate-900 font-medium block">
                      {new Date(user.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    <span className="text-slate-400 font-mono text-xs mt-0.5 block truncate">
                      {new Date(user.createdAt).toISOString()}
                    </span>
                  </div>

                  {/* Last Sign In */}
                  <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-slate-500 font-semibold block mb-1">Last Sign In</span>
                    {user.lastLogin ? (
                      <>
                        <span className="text-slate-900 font-medium block">
                          {new Date(user.lastLogin).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        <span className="text-slate-400 font-mono text-xs mt-0.5 block truncate">
                          {new Date(user.lastLogin).toISOString()}
                        </span>
                      </>
                    ) : (
                      <span className="text-slate-500 italic block">Never / Pending initial sign in</span>
                    )}
                  </div>

                  {/* Profile Updated */}
                  <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-slate-500 font-semibold block mb-1">Profile Updated</span>
                    <span className="text-slate-900 font-medium block">
                      {new Date(user.updatedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    <span className="text-slate-400 font-mono text-xs mt-0.5 block truncate">
                      {new Date(user.updatedAt).toISOString()}
                    </span>
                  </div>

                  {/* User ID */}
                  <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-slate-500 font-semibold block mb-1">User ID (UUID)</span>
                    <span className="font-mono text-slate-900 select-all break-all block" title={user.id}>
                      {user.id}
                    </span>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* TAB 4: CLASSROOM SESSIONS */}
            <TabsContent value="sessions" className="p-6 space-y-6 focus-visible:outline-none">
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs">
                <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
                  <h3 className="text-sm font-bold text-slate-900">
                    Classroom Sessions History ({sessions.length})
                  </h3>
                </div>

                {sessions.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {sessions.map((sess) => (
                      <div key={sess.id} className="py-3 flex items-center justify-between text-xs">
                        <div>
                          <span className="font-mono text-purple-700 font-semibold block">
                            {sess.id}
                          </span>
                          <span className="text-slate-500">
                            {new Date(sess.scheduled_start_time).toLocaleString()}
                          </span>
                        </div>
                        <span className="px-2 py-0.5 rounded font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {sess.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500 text-xs">
                    <Clock className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    No scheduled or completed classroom sessions recorded yet for this user.
                  </div>
                )}
              </div>
            </TabsContent>

            {/* TAB 5: SECURITY & AUDIT TRAIL */}
            <TabsContent value="audit" className="p-6 space-y-4 focus-visible:outline-none">
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs">
                <h3 className="text-sm font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">
                  Security & Audit Trace
                </h3>

                <div className="space-y-3 text-xs font-mono">
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2.5">
                    <span className="text-purple-700 font-semibold shrink-0">
                      [{new Date(user.createdAt).toISOString()}]
                    </span>
                    <span className="text-slate-700">
                      User record provisioned via Supabase Auth Admin.
                    </span>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2.5">
                    <span className="text-purple-700 font-semibold shrink-0">
                      [{new Date(user.createdAt).toISOString()}]
                    </span>
                    <span className="text-slate-700">
                      Trigger handle_new_user: Synchronized entity into public.profiles (role: {user.role}).
                    </span>
                  </div>

                  {user.candidateTutor && (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2.5">
                      <span className="text-purple-700 font-semibold shrink-0">
                        [{new Date(user.candidateTutor.created_at || user.createdAt).toISOString()}]
                      </span>
                      <span className="text-slate-700">
                        Candidate survey completed: candidate_tutor row upserted (status:{" "}
                        {user.candidateTutor.status}).
                      </span>
                    </div>
                  )}

                  {user.candidateStudent && (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2.5">
                      <span className="text-purple-700 font-semibold shrink-0">
                        [{new Date(user.candidateStudent.created_at || user.createdAt).toISOString()}]
                      </span>
                      <span className="text-slate-700">
                        Academic survey completed: candidate_student row upserted (level:{" "}
                        {user.candidateStudent.education_level}).
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ViewUser;
